/** @format */

import { useState } from "react";
import { uploadFileToS3, UploadResponse } from "../utils/uploadFileToS3";
import { api } from "../api/client";
import { getPdfImages } from "../utils/extractPDF";
import { formatTime } from "../utils/time";
import { useTrackChangesState } from "../store";
import { useNavigate } from "react-router-dom";
import { track } from "../lib/posthog";

function isUploadResponse(value: unknown): value is UploadResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    "upload_url" in value &&
    "media_id" in value &&
    typeof (value as UploadResponse).upload_url === "string" &&
    typeof (value as UploadResponse).media_id === "string"
  );
}

const useSopAnalyze = (
  files: File[],
  text: string,
  fileNotes: Record<string, string> = {},
  assemblyFile: File | null = null,
  onSubmitSuccess?: () => void,
) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [analysisDuration, setAnalysisDuration] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [formattedElapsedTime, setFormattedElapsedTime] =
    useState<string>("00:00");
  const { setSelectedSOP, fetchSOPs } = useTrackChangesState();
  const navigate = useNavigate();

  const analyzeFile = async () => {
    if (files.length === 0) {
      return;
    }

    const startTime = Date.now();
    setAnalyzing(true);
    setProgressMessage("Starting analysis...");
    setProgressPercentage(0);
    setError(null);
    setAnalysisDuration(null);
    setElapsedTime(0);
    setFormattedElapsedTime("00:00");

    const fileName = files[0]?.name.toLowerCase() ?? "";
    const isImage = files[0]?.type.startsWith("image");
    const isStep = fileName.endsWith(".stp") || fileName.endsWith(".step");
    const isVideo = files[0]?.type.startsWith("video");
    const flowType = isStep ? "cad" : isVideo ? "video" : isImage ? "image" : "drawings";
    track("sop_file_uploaded", {
      type: "new_sop",
      flow_type: flowType,
      file_count: files.length,
    });

    // Start elapsed time counter
    const elapsedInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(elapsed);
      setFormattedElapsedTime(formatTime(elapsed));
    }, 1000);

    // upload files to S3
    try {
      let fileUrls: string[] = [];
      let uploadResponses: (UploadResponse | string | undefined)[] = [];
      let assemblyRefId: string | null = null;

      // Upload assembly file if it exists
      if (assemblyFile) {
        setProgressMessage("Uploading assembly PDF...");
        setProgressPercentage(5);

        const assemblyResponse = await uploadFileToS3(
          assemblyFile,
          undefined,
          (progress) => {
            setProgressPercentage(Math.round((progress / 100) * 10)); // First 10% for assembly
          },
        );

        if (isUploadResponse(assemblyResponse)) {
          assemblyRefId = assemblyResponse.media_id;
        }
      }

      if (isImage || isStep) {
        setProgressMessage(
          isImage ? "Processing images..." : "Processing STEP file...",
        );
        setProgressPercentage(0);

        uploadResponses = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const fileProgress = (i / files.length) * 100;

          const response = await uploadFileToS3(file, undefined, (progress) => {
            const overallProgress =
              fileProgress + (progress / 100) * (100 / files.length);
            setProgressPercentage(Math.round(overallProgress));
          });

          uploadResponses.push(response);
        }

        fileUrls = uploadResponses
          .filter(
            (response): response is UploadResponse =>
              response !== undefined &&
              typeof response !== "string" &&
              isUploadResponse(response),
          )
          .map((response) => response.media_id);
      } else if (isVideo) {
        setProgressMessage("Processing video...");
        setProgressPercentage(0);

        uploadResponses = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const fileProgress = (i / files.length) * 100;

          const response = await uploadFileToS3(file, undefined, (progress) => {
            const overallProgress =
              fileProgress + (progress / 100) * (100 / files.length);
            setProgressPercentage(Math.round(overallProgress));
          });

          uploadResponses.push(response);
        }

        fileUrls = uploadResponses
          .filter(
            (response): response is UploadResponse =>
              response !== undefined &&
              typeof response !== "string" &&
              isUploadResponse(response),
          )
          .map((response) => response.media_id);
      } else {
        setProgressMessage("Processing PDF...");
        setProgressPercentage(10);
        const pdfImagesArrays = await Promise.all(
          files.map(async (file) => {
            const images = await getPdfImages(file);
            return images || [];
          }),
        );

        setProgressMessage("Uploading PDF images...");
        setProgressPercentage(20);

        const allPdfImages = pdfImagesArrays.flat();
        uploadResponses = [];
        for (let i = 0; i < allPdfImages.length; i++) {
          const file = allPdfImages[i];
          const fileProgress = 20 + (i / allPdfImages.length) * 80;

          const response = await uploadFileToS3(file, undefined, (progress) => {
            const overallProgress =
              fileProgress + (progress / 100) * (80 / allPdfImages.length);
            setProgressPercentage(Math.round(overallProgress));
          });

          uploadResponses.push(response);
        }

        fileUrls = uploadResponses
          .filter(
            (response): response is UploadResponse =>
              response !== undefined &&
              typeof response !== "string" &&
              isUploadResponse(response),
          )
          .map((response) => response.media_id);
      }

      if (fileUrls.length === 0) return;

      // const selectedCategory = category.match(/\(([^)]+)\)/)?.[1] || category;

      setProgressMessage("Starting analysis...");

      let response = null;
      if (isStep) {
        response = await api.post("/upload", {
          category: "SOP",
          imageRefIds: [],
          stepRefIds: fileUrls,
          assemblyRefId: assemblyRefId,
        });
      } else if (isVideo) {
        // Create videoTexts array that matches the order of videoRefIds
        const videoTexts = fileUrls.map((mediaId) => {
          // Find the file that corresponds to this mediaId
          const fileIndex = uploadResponses.findIndex(
            (response) =>
              isUploadResponse(response) && response.media_id === mediaId,
          );
          if (fileIndex !== -1) {
            const fileName = files[fileIndex].name;
            return fileNotes[fileName] || "";
          }
          return "";
        });

        response = await api.post("/upload", {
          category: "SOP",
          imageRefIds: [],
          videoRefIds: fileUrls,
          text: text,
          videoTexts: videoTexts,
          assemblyRefId: assemblyRefId,
        });
      } else {
        response = await api.post("/upload", {
          category: "SOP",
          imageRefIds: fileUrls,
          assemblyRefId: assemblyRefId,
        });
      }

      if (response.status === 202) {
        const jobData = response.data;
        const sopId = jobData.sopId;

        if (sopId) {
          track("sop_created", {
            sop_id: sopId,
            flow_type: flowType,
            file_count: files.length,
          });
          clearInterval(elapsedInterval);
          setSelectedSOP(sopId);
          await fetchSOPs();
          if (onSubmitSuccess) {
            onSubmitSuccess();
            setTimeout(() => navigate("/sop-dashboard"), 1500);
          } else {
            navigate("/sop-dashboard");
          }
          return;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      clearInterval(elapsedInterval);
      console.error("Full error:", err);
      console.error("Error response data:", err.response?.data);
      console.error("Error object:", err.response?.data?.error);

      const errorType =
        err.response?.data?.error?.type ??
        err.response?.data?.error ??
        "unknown_error";
      track("app_error_occurred", {
        error_type: typeof errorType === "string" ? errorType : "api_error",
        error_location: "upload",
        severity: "high",
      });

      if (err.response?.data?.error?.type === "ValidationException") {
        setError(err.response.data.error.message);
      } else if (
        err.response?.data?.error?.message?.includes(
          "Read timeout on endpoint URL",
        )
      ) {
        setError(
          "There was an error attempting to contact the AI. Please try again later.",
        );
      } else if (err.response?.data?.error === "free_trial_limit_reached") {
        setError(
          "You have reached your free trial limit. Please upgrade to a paid plan to continue.",
        );
      } else {
        setError(
          err.response?.data?.error?.message ||
            "An error occurred while analyzing the files.",
        );
      }
    } finally {
      clearInterval(elapsedInterval);
      setAnalyzing(false);
      setProgressMessage("");
      setProgressPercentage(0);
    }
  };

  return {
    analyzing,
    analyzeFile,
    progressMessage,
    progressPercentage,
    error,
    analysisDuration,
    elapsedTime,
    formattedElapsedTime,
  };
};

export default useSopAnalyze;
